import {Color} from "three";

import type {ColorOrCurve, SizeOrCurve} from "./types";

// Helper to generate a random number between min and max
export const random = (min: number, max: number): number =>
  Math.random() * (max - min) + min;

/**
 * Generates a random point on the surface of a sphere for a uniform directional vector.
 */
export const randomOnSphere = (
  radius: number = 1,
): [number, number, number] => {
  // A more efficient method than rejection sampling
  const u = Math.random();
  const v = Math.random();
  const theta = 2 * Math.PI * u;
  const phi = Math.acos(2 * v - 1);
  const x = radius * Math.sin(phi) * Math.cos(theta);
  const y = radius * Math.sin(phi) * Math.sin(theta);
  const z = radius * Math.cos(phi);
  return [x, y, z];
};

/**
 * Interpolates a value along a curve defined by stops.
 * @param progress - The current progress (0 to 1).
 * @param stops - An array of stops, which must be sorted by the `stop` property.
 * @param lerpFn - The function to use for interpolation.
 * @returns The interpolated value.
 */
export function interpolateCurve<T>(
  progress: number,
  stops: {stop: number; value: T}[],
  lerpFn: (a: T, b: T, t: number) => T,
): T {
  if (stops.length === 0) throw new Error("Curve must have at least one stop.");
  if (stops.length === 1) return stops[0]!.value;

  if (progress <= stops[0]!.stop) return stops[0]!.value;
  if (progress >= stops[stops.length - 1]!.stop)
    return stops[stops.length - 1]!.value;

  for (let i = 0; i < stops.length - 1; i++) {
    const current = stops[i]!;
    const next = stops[i + 1]!;
    if (progress >= current.stop && progress < next.stop) {
      const t = (progress - current.stop) / (next.stop - current.stop);
      return lerpFn(current.value, next.value, t);
    }
  }

  return stops[stops.length - 1]!.value; // Should be unreachable
}

/**
 * Normalizes a color or a color curve into a consistent curve format.
 */
export function normalizeColorCurve(
  colorOrCurve: ColorOrCurve,
): {stop: number; value: Color}[] {
  if (Array.isArray(colorOrCurve)) {
    return colorOrCurve.map(({stop, color}) => ({stop, value: color}));
  }
  // If a single color is provided, create a flat curve.
  return [
    {stop: 0, value: colorOrCurve},
    {stop: 1, value: colorOrCurve},
  ];
}

/**
 * Normalizes a size or a size curve into a consistent curve format.
 */
export function normalizeSizeCurve(
  sizeOrCurve: SizeOrCurve,
): {stop: number; value: number}[] {
  if (Array.isArray(sizeOrCurve)) {
    return sizeOrCurve.map(({stop, size}) => ({stop, value: size}));
  }
  // If a single number is provided, create a flat curve.
  return [
    {stop: 0, value: sizeOrCurve},
    {stop: 1, value: sizeOrCurve},
  ];
}
